// LLM-assisted email triage: classifies emails against user-defined rules and
// applies the matched rule's action (move / categorize). Move and categorize are
// non-destructive (reversible), so batch/auto-apply runs write directly without a
// confirmation dialog. See
// docs/superpowers/plans/2026-07-02-gap-4-triage-auto-categorization.md.

import { sendChat } from "./ai-client";
import { stripCodeFence } from "./json-extract";
import type { AddinSettings } from "./settings";
import type { Rule } from "./triage-rules";
import type { MailboxAdapter, EmailSummary } from "./mailbox-adapter";
import type { OrgPolicy } from "./policy";
import { assertSafeToSendToAI } from "./data-classification";
import { debugLog } from "./debug";

/** The rule (if any) that matched, and the action to apply — derived from the
 * matched rule's own `action`, never from free-form model output, so a model
 * cannot be prompt-injected into moving mail to an arbitrary destination. */
export interface ClassificationResult {
  ruleId: string;
  folder?: string;
  categories?: string[];
}

function isClassificationShape(value: unknown): value is { ruleId: string | null } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  if (!("ruleId" in candidate)) {
    return false;
  }
  return candidate.ruleId === null || typeof candidate.ruleId === "string";
}

function buildClassificationPrompt(email: EmailSummary, rules: Rule[]): string {
  const ruleLines = rules
    .map((r) => `- id: ${r.id}\n  name: ${r.name}\n  when: ${r.when}`)
    .join("\n");

  return `You are triaging a single email against a list of user-defined assignment rules.
Decide whether ANY rule's "when" condition semantically matches this email's subject/sender/preview.
Respond with JSON only — no prose, no markdown code fence — matching exactly this shape:
{"ruleId": "<id of the single best-matching rule>"} if a rule matches, or {"ruleId": null} if none matches.
Only ever return a ruleId from the list below; never invent one.

Rules:
${ruleLines || "(no rules defined)"}

Email:
Subject: ${email.subject}
From: ${email.from}
Preview: ${email.preview}`;
}

/**
 * Evaluates a single email against the user's rules via the LLM. Only the
 * matched rule's *id* is trusted from the model's response — the actual
 * `folder`/`categories` action is looked up from the matching `Rule` in
 * `rules`, never taken verbatim from the model's output. Returns `null` when
 * no rule matches (including when the model names a rule ID that doesn't
 * exist in `rules` — treated as no match rather than trusted blindly).
 * Throws a clear error when the model's response isn't parseable JSON with
 * the expected shape, so callers can surface (not silently ignore) it. Also
 * throws `BlockedByClassificationError` (data-classification.ts, ADR-005)
 * before building the prompt when the email's sensitivity classification
 * disallows AI processing — `runTriage` already records per-email failures
 * into its `errors` list, so this surfaces the same way as any other
 * per-email classification failure.
 */
export async function classifyEmail(
  email: EmailSummary,
  rules: Rule[],
  settings: AddinSettings,
  adapter: Pick<MailboxAdapter, "getSensitivityLabel">,
  policy: OrgPolicy = {}
): Promise<ClassificationResult | null> {
  if (rules.length === 0) {
    return null;
  }

  await assertSafeToSendToAI(email.id, email.subject, email.preview, adapter, settings, policy);

  const prompt = buildClassificationPrompt(email, rules);
  const response = await sendChat([{ role: "system", content: prompt }], [], settings);
  const raw = response.content ?? "";

  let parsed: unknown;
  try {
    parsed = JSON.parse(stripCodeFence(raw));
  } catch {
    throw new Error(`Could not parse the triage classification for "${email.subject}".`);
  }

  if (!isClassificationShape(parsed)) {
    throw new Error(`Could not parse the triage classification for "${email.subject}".`);
  }

  if (parsed.ruleId === null) {
    return null;
  }

  const rule = rules.find((r) => r.id === parsed.ruleId);
  if (!rule) {
    debugLog("triage:unknown-rule-id", { emailId: email.id });
    return null;
  }

  return { ruleId: rule.id, folder: rule.action.folder, categories: rule.action.categories };
}

/** A proposed (not-yet-applied) triage action for one email. */
export interface TriageProposal {
  itemId: string;
  subject: string;
  ruleId: string;
  ruleName: string;
  folder?: string;
  categories?: string[];
}

export interface TriageError {
  itemId: string;
  message: string;
}

export interface TriageRunResult {
  /** Every proposal generated by classification, applied or not. */
  proposals: TriageProposal[];
  /** Proposals actually written to the mailbox (empty in review mode). */
  applied: TriageProposal[];
  errors: TriageError[];
}

export interface RunTriageOptions {
  /** false (default review mode): only return proposals, never write anything. */
  autoApply: boolean;
}

export interface ApplyProposalsResult {
  applied: TriageProposal[];
  errors: TriageError[];
}

/**
 * Applies a set of already-classified proposals — no LLM call here, just the
 * writes. Move and categorize are non-destructive (reversible), so proposals
 * are written directly with no confirmation dialog. Exported separately from
 * `runTriage` so a UI that already has reviewed proposals (e.g. `RulesPanel`'s
 * review-mode "Apply" action) can apply them directly without re-running
 * classification. A per-proposal write failure is recorded in `errors` and
 * does not abort the rest of the batch.
 */
export async function applyProposals(
  proposals: TriageProposal[],
  adapter: MailboxAdapter
): Promise<ApplyProposalsResult> {
  const errors: TriageError[] = [];
  if (proposals.length === 0) {
    return { applied: [], errors };
  }

  const applied: TriageProposal[] = [];
  for (const proposal of proposals) {
    try {
      if (proposal.categories && proposal.categories.length > 0) {
        await adapter.applyCategories(proposal.itemId, proposal.categories);
      }
      if (proposal.folder) {
        const folders = await adapter.listFolders();
        const target = folders.find((f) => f.displayName === proposal.folder);
        if (!target) {
          throw new Error(`Folder not found: ${proposal.folder}`);
        }
        await adapter.moveEmail(proposal.itemId, target.id);
      }
      applied.push(proposal);
    } catch (error) {
      errors.push({
        itemId: proposal.itemId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return { applied, errors };
}

/**
 * Classifies a batch of emails against `rules`, then — only when `autoApply`
 * is true — hands the resulting proposals to {@link applyProposals} (which
 * writes them directly; move/categorize are non-destructive). In review
 * mode (`autoApply: false`, the default UI behavior), nothing is ever
 * written: the caller renders `proposals` for the user to accept manually.
 * A per-email classification failure is recorded in `errors` and does not
 * abort the rest of the batch.
 */
export async function runTriage(
  items: EmailSummary[],
  rules: Rule[],
  settings: AddinSettings,
  adapter: MailboxAdapter,
  options: RunTriageOptions,
  policy: OrgPolicy = {}
): Promise<TriageRunResult> {
  const proposals: TriageProposal[] = [];
  const errors: TriageError[] = [];

  for (const item of items) {
    try {
      const classification = await classifyEmail(item, rules, settings, adapter, policy);
      if (!classification) {
        continue;
      }
      const rule = rules.find((r) => r.id === classification.ruleId);
      proposals.push({
        itemId: item.id,
        subject: item.subject,
        ruleId: classification.ruleId,
        ruleName: rule?.name ?? classification.ruleId,
        folder: classification.folder,
        categories: classification.categories,
      });
    } catch (error) {
      errors.push({
        itemId: item.id,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  if (!options.autoApply || proposals.length === 0) {
    return { proposals, applied: [], errors };
  }

  const { applied, errors: applyErrors } = await applyProposals(proposals, adapter);

  return { proposals, applied, errors: [...errors, ...applyErrors] };
}

/**
 * Shows a confirmation dialog for a destructive action (folder/email delete)
 * via `Office.context.ui.displayDialogAsync`. This opens a page OUTSIDE the
 * task pane iframe — the anti-clickjacking pattern required for sensitive
 * operations (see CLAUDE.md B3) — so a malicious page embedding the add-in
 * cannot spoof or auto-click the confirmation. Resolves `false` (never
 * applies) on any failure to open the dialog, the user closing it, or an
 * unparseable response.
 */
export function confirmBatchAction(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const url = `${window.location.origin}/triage-confirm-dialog.html?message=${encodeURIComponent(
      message
    )}`;
    Office.context.ui.displayDialogAsync(
      url,
      { height: 40, width: 40, displayInIframe: false },
      (asyncResult: Office.AsyncResult<Office.Dialog>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          debugLog("triage:dialog-failed", { code: asyncResult.error?.code });
          resolve(false);
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;
        const settle = (confirmed: boolean) => {
          if (settled) return;
          settled = true;
          try {
            dialog.close();
          } catch {
            // Dialog may already be closed by the user — ignore.
          }
          resolve(confirmed);
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
          const raw = (arg as { message?: string }).message ?? "";
          let confirmed = false;
          try {
            confirmed = Boolean((JSON.parse(raw) as { confirmed?: boolean }).confirmed);
          } catch {
            confirmed = false;
          }
          settle(confirmed);
        });

        // Fired when the dialog is closed/blocked without sending a message
        // (e.g. the user closes the window) — treat as a decline, never as consent.
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          settle(false);
        });
      }
    );
  });
}
